import { promisefy } from '../utils/promisefy';
import { ensureOBSPresence } from '../utils/ensureOBSPresence';
import { ScopedStorage } from './ScopedStorageService';
import { AudioCompositionId, AudioCompositionInfo, ReadScopedStorageItemInfoResult } from '../types/obs';
import { z } from 'zod';

export type AudioCompositionOrAudioCompositionId = AudioCompositionInfo | AudioCompositionId;

export class AudioCompositionStorageNotFoundError extends Error {}

export const recordingSettingsValidationSchema = z.object({
  useRecordingSettingsAsOBS: z.boolean().default(false),
  recordingFileFormat: z
    .string({
      required_error: 'File format is required',
    })
    .default('%CCYY%MM%DD - %hh%mm%ss'),
  recordingAudioEncoderClass: z.string().default('ffmpeg_aac'),
  recordingAudioTracks: z.array(z.number()).default([0]),
  recordingBitrate: z.coerce.number().default(128),
  useReplayBuffer: z.boolean().default(false),
  replayBufferMaxSeconds: z.number().default(20),
});

export type RecordingSettings = z.infer<typeof recordingSettingsValidationSchema>;

export class AudioCompositionsService {
  static async getAllAudioCompositions() {
    return promisefy(window.host.getAllAudioCompositions)();
  }

  static getRecordingCompositionId(id: AudioCompositionId) {
    if (id.startsWith('recording-')) {
      return id;
    }

    return `recording-${id}`;
  }

  private static getRecordingSettingsScopedStorageIdObject(id: AudioCompositionId) {
    return {
      container: 'recording-settings',
      scope: 'streamelements_multi-streaming',
      item: id,
    };
  }

  static getScopedStorageIdObject(id: string) {
    return {
      container: 'audio-compositions',
      scope: 'streamelements_multi-streaming',
      item: id,
    };
  }

  static async getCompositionsFromStorage() {
    await ensureOBSPresence();
    const allItems = await ScopedStorage.getAllScopedStorageJsonItems({
      scope: 'streamelements_multi-streaming',
      container: 'audio-compositions',
    });

    const compositions = await Promise.allSettled(
      allItems.filter(({ item }) => item !== 'scene-links').map(({ item }) => this.getCompositionFromStorageById(item))
    );

    return Object.fromEntries(
      compositions.filter((c) => c.status === 'fulfilled').map((c) => [c?.value?.item, c?.value?.content]).filter((c) => c[1])
    );
  }

  static async getCompositionFromStorageById(id: string) {
    await ensureOBSPresence();
    return await ScopedStorage.readScopedStorageJsonItem<AudioCompositionInfo>(this.getScopedStorageIdObject(id));
  }

  static async storeComposition(
    input: string | AudioCompositionInfo
  ): Promise<ReadScopedStorageItemInfoResult<AudioCompositionInfo>> {
    await ensureOBSPresence();

    const composition = typeof input === 'string' ? await this.getCompositionFromMemory(input) : input;

    if (!composition) {
      throw new Error('Composition not found');
    }

    return ScopedStorage.writeScopedStorageJsonItem({
      ...AudioCompositionsService.getScopedStorageIdObject(composition.id),
      content: composition,
    });
  }

  static async storeRecordingSettings({
    audioCompositionId,
    settings,
  }: {
    audioCompositionId: AudioCompositionId;
    settings: RecordingSettings;
  }) {
    await ensureOBSPresence();

    return ScopedStorage.writeScopedStorageJsonItem({
      ...AudioCompositionsService.getRecordingSettingsScopedStorageIdObject(
        AudioCompositionsService.getRecordingCompositionId(audioCompositionId)
      ),
      content: settings,
    });
  }

  static async getRecordingSettings({ audioCompositionId }: { audioCompositionId: AudioCompositionId }): Promise<RecordingSettings> {
    await ensureOBSPresence();

    const result = await ScopedStorage.readScopedStorageJsonItem<{
      recordingFileFormat: string;
      recordingAudioEncoderClass: string;
      audioTracks: number[];
      useRecordingSettingsAsOBS: boolean;
      recordingBitrate: number;
      useReplayBuffer: number,
      replayBufferMaxSeconds: number,
    }>(
      AudioCompositionsService.getRecordingSettingsScopedStorageIdObject(
        this.getRecordingCompositionId(audioCompositionId)
      )
    );

    const validationResult = recordingSettingsValidationSchema.safeParse(result?.content);

    if (!validationResult.success) {
      if (result) {
        throw new Error('Audio composition recording settings are malformed.');
      }

      const updatedSettings = {
        recordingAudioTracks: [0],
        useRecordingSettingsAsOBS: false,
        recordingAudioEncoderClass: 'ffmpeg_aac',
        recordingFileFormat: '%CCYY%MM%DD - %hh%mm%ss',
        recordingBitrate: 128,
        useReplayBuffer: false,
        replayBufferMaxSeconds: 20,
      };

      await this.storeRecordingSettings({ audioCompositionId, settings: updatedSettings });
      return updatedSettings;
    }

    return validationResult.data;
  }

  static async removeRecordingSettings(id: AudioCompositionId) {
    await ensureOBSPresence();
    return ScopedStorage.removeScopedStorageJsonItem({
      ...AudioCompositionsService.getRecordingSettingsScopedStorageIdObject(id),
    });
  }

  static async registerComposition(input: Omit<AudioCompositionInfo, 'canRemove' | 'isObsNativeComposition'>) {
    const { id: audioCompositionId, type, ...composition } = input;

    const registeredComposition = await promisefy(window.host.addAudioComposition)({
      ...composition,
      id:
        type === 'recording'
          ? AudioCompositionsService.getRecordingCompositionId(audioCompositionId)
          : audioCompositionId,
    });

    if (!registeredComposition) {
      throw new Error('Failed to create composition');
    }

    console.log('Composition registered', registeredComposition);

    await this.storeComposition(registeredComposition);

    return registeredComposition;
  }

  static async removeCompositionStorage(id: string) {
    await ensureOBSPresence();
    return ScopedStorage.removeScopedStorageJsonItem({
      ...AudioCompositionsService.getScopedStorageIdObject(id),
    });
  }

  static async removeComposition(params: { audioCompositionId: AudioCompositionId }) {
    await ensureOBSPresence();
    await promisefy(window.host.removeAudioCompositionsByIds)([params.audioCompositionId]);
    await this.removeCompositionStorage(params.audioCompositionId);
  }

  static async getActiveCompositions<R extends Record<AudioCompositionId, AudioCompositionInfo>>(): Promise<R> {
    await ensureOBSPresence();
    const storage = await this.getCompositionsFromStorage();
    const memory = await promisefy(window.host.getAllAudioCompositions)();

    return {
      ...storage,
      ...memory,
    } as R;
  }

  static async getCompositionFromMemory(id: AudioCompositionId) {
    await ensureOBSPresence();
    const result = await promisefy(window.host.getAllAudioCompositions)();
    const composition = result[id];

    if (!composition) {
      return null;
    }

    return composition;
  }

  static async loadAudioComposition(params: { audioCompositionId: AudioCompositionId }) {
    console.log('Loading audio composition', params.audioCompositionId);

    await ensureOBSPresence();

    const composition = await this.getCompositionFromMemory(params.audioCompositionId);

    if (composition) {
      console.info('Composition exists in memory', composition);
      return composition;
    }

    const storedComposition = await this.getCompositionFromStorageById(params.audioCompositionId);

    if (!storedComposition) {
      throw new AudioCompositionStorageNotFoundError('Composition does not exist in storage');
    }

    console.info('Composition exists in storage, but not in memory. Registering...');
    const registeredComposition = await this.registerComposition(storedComposition.content);

    console.log('Composition registered', registeredComposition);

    return registeredComposition;
  }

  static async getRecordingComposition(id: AudioCompositionId) {
    return this.getCompositionFromMemory(`recording-${id}`);
  }

  static async isCompositionInMemory(id: AudioCompositionId) {
    return !!this.getCompositionFromMemory(id);
  }
}
